import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormField, applyWhen, form, maxLength, required } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';

import { ImmomioAnchorComponent, ImmomioButtonComponent } from '@immomio/ui/button';
import { ImmomioChipFilterComponent } from '@immomio/ui/chip-filter';
import { ImmomioSignalFormFieldComponent } from '@immomio/ui/form';
import { ImmomioModal, ImmomioModalBase, ImmomioModalHeader } from '@immomio/ui/modal';

import { ImmomioIconName } from '@resident-nx/shared';
import { FeedbackStore } from '@resident-nx/web/feedback/data-access';
import { IconWebComponent } from '@resident-nx/web/ui';
import { ResidentFeedbackCategory } from '@resident-nx/web/feedback/model';

interface FeedbackFormModel {
  category: ResidentFeedbackCategory | null;
  comment: string;
}

interface FeedbackCategoryItem {
  value: ResidentFeedbackCategory;
  label: string;
  icon?: ImmomioIconName;
}

const FEEDBACK_COMMENT_MAX_LENGTH = 500;

const FEEDBACK_CATEGORIES: FeedbackCategoryItem[] = [
  { value: 'BUG', label: 'feedback_prompt.category_bug_l', icon: ImmomioIconName.Hammer },
  { value: 'IDEA', label: 'feedback_prompt.category_idea_l', icon: ImmomioIconName.LightBulb },
  { value: 'UX', label: 'feedback_prompt.category_ux_l', icon: ImmomioIconName.Keyboard },
  { value: 'OTHER', label: 'feedback_prompt.category_other_l' },
];

@Component({
  selector: 'rs-web-feedback-modal',
  templateUrl: './feedback-modal.component.html',
  styleUrl: './feedback-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImmomioModal,
    ImmomioModalHeader,
    ImmomioButtonComponent,
    ImmomioChipFilterComponent,
    ImmomioSignalFormFieldComponent,
    IconWebComponent,
    FormField,
    FormsModule,
    TranslatePipe,
    ImmomioAnchorComponent,
  ],
})
export class FeedbackModalComponent extends ImmomioModalBase {
  protected readonly ImmomioIconName = ImmomioIconName;
  protected readonly categories = FEEDBACK_CATEGORIES;
  protected readonly maxCommentLength = FEEDBACK_COMMENT_MAX_LENGTH;

  protected readonly store = inject(FeedbackStore);

  protected readonly model = signal<FeedbackFormModel>({ category: null, comment: '' });

  protected readonly feedbackForm = form(this.model, path => {
    maxLength(path.comment, FEEDBACK_COMMENT_MAX_LENGTH);
    // AC 3.1: picking a category makes the comment mandatory.
    applyWhen(
      path,
      ({ value }) => value().category !== null,
      p => required(p.comment)
    );
  });

  /** `im-chip-filter` is a multi-select CVA; the backend takes a single category. */
  protected readonly selectedCategories = computed(() => {
    const category = this.model().category;
    return category ? [category] : [];
  });

  /** Chip icons keyed by category value, so the template can index instead of calling. */
  protected readonly categoryIcons = computed(
    () =>
      Object.fromEntries(
        FEEDBACK_CATEGORIES.filter(category => category.icon).map(category => [
          category.value,
          category.icon,
        ])
      ) as Record<string, ImmomioIconName | undefined>
  );

  protected readonly commentLength = computed(() => this.model().comment.length);

  protected readonly canSubmit = computed(() => !this.feedbackForm().invalid());

  /** Keeps only the last picked chip so the multi-select CVA behaves as single-select. */
  protected onCategoriesChange(values: string[]): void {
    const category = (values.at(-1) as ResidentFeedbackCategory | undefined) ?? null;
    this.model.update(model => ({ ...model, category }));
  }

  protected onDismiss(): void {
    this.dismissModal();
  }

  protected onPositive(): void {
    this.store.choosePositive();
  }

  protected onNegative(): void {
    this.store.chooseNegative();
  }

  protected onSubmit(): void {
    const { category, comment } = this.model();
    this.store.addDetails({ category, comment: comment.trim() });
  }

  protected onDone(): void {
    this.closeModal();
  }

  /**
   * The single write for this prompt. Runs for every exit path (X, ESC, backdrop, "Fertig")
   * and reports whichever step the user reached.
   */
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.store.report();
  }
}
